import React, { createContext, useContext } from "react";
import { MessageType } from "types";
import { Constants, Enums } from "utils";

interface AppContextType {
  t: any;
  locale: string;
  locales: string[];
  setLocale: (language: string) => void;
  hasFeedback: boolean;
  feedback: MessageType;
  setFeedback: (feedback: MessageType) => void;
  unsetFeedback: () => void;
}

const initialState = {
  locale: "EN",
  hasFeedback: false,
  translations: Constants.LOCALE_STRINGS[Constants.LOCALE.EN],
  locales: Object.keys(Constants.LOCALE) as string[],
  feedback: { message: "", severity: Enums.EnumFeedback.Info },
};

export const AppContext = createContext<AppContextType>({
  t: initialState.translations,
  locale: initialState.locale,
  locales: initialState.locales,
  setLocale: () => { },
  hasFeedback: initialState.hasFeedback,
  feedback: initialState.feedback,
  setFeedback: () => { },
  unsetFeedback: () => { },
});

interface Props {
  initLocale: string;
  children?: React.ReactNode;
}

export const AppProvider = React.memo<Props>(
  ({ initLocale, children }: Props) => {
    const [locale, setLocale] = React.useState<string>(initLocale);
    const [hasFeedback, setHasFeedback] = React.useState<boolean>(
      initialState.hasFeedback
    );
    const [feedback, setFeedback] = React.useState<MessageType>(
      initialState.feedback
    );

    const setFeedbackCallback = (newFeedback: MessageType) => {
      if (!hasFeedback) {
        setHasFeedback(true);
      }

      setFeedback(newFeedback);
    };

    const unsetFeedback = () => {
      setFeedback(initialState.feedback);
      setHasFeedback(false);
    };

    const MemoizedValue = React.useMemo(() => {
      const value: AppContextType = {
        locale,
        t: Constants.LOCALE_STRINGS[locale],
        setLocale,
        locales: initialState.locales,
        hasFeedback,
        feedback,
        setFeedback: setFeedbackCallback,
        unsetFeedback,
      };
      return value;
    }, [feedback, hasFeedback, locale]);

    return (
      <AppContext.Provider value={MemoizedValue}>
        {children}
      </AppContext.Provider>
    );
  }
);

export const useApp = () => useContext(AppContext);
